import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';

import { RacingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { WinGameFixture } from '@frontend/sports/racing/feature/models';
import { firstValueFrom } from 'rxjs';

import { GreyhoundRacingApiService } from '../../../horse-racing-shared/services/greyhound-racing-api.service';
import { HorseRacingApiService } from '../../../horse-racing-shared/services/horse-racing-api.service';
import { HorsesFixturePricingHelper } from '../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { EachWayMarketHelperService } from '../../../option-group/each-way-market-helper.service';
import { FixtureUpdateService } from '../../common/fixture-update.service';
import { HorseRacingTrackingService } from '../../common/horse-racing-tracking.service';
import { RacingService } from '../../services/racing-service';
import { nextRacesCardAnimation } from './animation';
import { NextRacesBaseComponent } from './next-races-base.component';

@Component({
    selector: 'ms-next-races',
    templateUrl: 'next-races.component.html',
    styleUrls: ['./next-races.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [nextRacesCardAnimation],
    standalone: false,
})
export class NextRacesComponent extends NextRacesBaseComponent implements OnInit {
    override filterOutAll = true;
    @ViewChild(ScrollAdapterComponent) scrollAdapter?: ScrollAdapterComponent;

    constructor(
        public sitecore: Sitecore,
        private urlHelper: UrlHelperService,
        private horseRacingTrackingService: HorseRacingTrackingService,
        protected override horseRacingService: HorseRacingApiService,
        protected override greyhoundRacingService: GreyhoundRacingApiService,
        protected override changeDetector: ChangeDetectorRef,
        protected override layoutService: AdaptiveLayoutService,
        protected override racingConfig: RacingConfig,
        protected override racingService: RacingService,
        protected override eachWayMarketHelper: EachWayMarketHelperService,
        protected override horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected override fixtureUpdateService: FixtureUpdateService,
    ) {
        super(
            changeDetector,
            horseRacingService,
            greyhoundRacingService,
            racingConfig,
            racingService,
            layoutService,
            horsesFixturePricingHelper,
            eachWayMarketHelper,
            fixtureUpdateService,
        );
    }

    ngOnInit(): void {
        this.nextCount = this.racingConfig.nextFixturesCount;
        this.trackRaces();
    }

    protected trackRaces(): void {
        const fixtures = Array.from(this.fixtures.values());
        this.horseRacingTrackingService.trackNextRaces(fixtures as WinGameFixture[]);
    }

    getFixtureLink(fixture: WinGameFixture): string {
        return this.urlHelper.getRacingUrlByFixture(fixture.sport.id, fixture);
    }

    filterOut(filterOutAll: boolean): void {
        this.filterOutAll = filterOutAll;
        this.refreshData();
        this.horseRacingTrackingService.trackfilter(
            filterOutAll ? this.sitecore.horseRacing.nextThreeRaces.All : this.sitecore.horseRacing.nextThreeRaces.UKIreland,
        );
    }

    protected async refreshData(): Promise<void> {
        const fixtures = this.filterOutAll
            ? await firstValueFrom(this.horseRacingService.getNextFixtures())
            : await firstValueFrom(this.horseRacingService.getNextFixtures(undefined, this.racingConfig.filterRegionIds));
        this.scrollAdapter?.resetArrows();
        setTimeout(() => {
            this.fixtures.clear();
            this.subscribeToFixtures(fixtures);
            this.updateMarketTitles(fixtures, true);
            this.changeDetector.markForCheck();
        }, 200);
    }

    track(event: string): void {
        this.horseRacingTrackingService.trackArrows(event);
    }
}
